import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// Верх дашборда: асики одним блоком, аптайм, потребление, сумма, хешрейт по алгоритмам и график.
// Всё считается по устройствам, которые сейчас показаны в таблице (фильтры и галочки).

const UNIT_TO_H = { 'KH/s': 1e3, 'MH/s': 1e6, 'GH/s': 1e9, 'TH/s': 1e12, 'PH/s': 1e15 };

const toDate = (v) => (v?.toDate ? v.toDate() : new Date(v));

// value в TH/s (так отдаёт подготовка графика в Dashboard.js)
export const formatHashrateTh = (value, algorithm) => {
  if (value == null || isNaN(value)) return '—';
  if (Number(value) === 0) return '0';
  if (algorithm === 'Equihash') {
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)} PH/s`;
    if (value >= 1000) return `${(value / 1000).toFixed(2)} TH/s`;
    return `${value.toFixed(2)} kSol/s`;
  }
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)} EH/s`;
  if (value >= 1000) return `${(value / 1000).toFixed(2)} PH/s`;
  if (value >= 1) return `${value.toFixed(2)} TH/s`;
  if (value * 1000 >= 1) return `${(value * 1000).toFixed(2)} GH/s`;
  return `${(value * 1e6).toFixed(2)} MH/s`;
};

const mergeDuration = (periods) => {
  if (periods.length === 0) return 0;
  const sorted = [...periods].sort((a, b) => a.start - b.start);
  let total = 0;
  let cur = { ...sorted[0] };
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start <= cur.end) cur.end = Math.max(cur.end, sorted[i].end);
    else { total += cur.end - cur.start; cur = { ...sorted[i] }; }
  }
  return total + (cur.end - cur.start);
};

// Аптайм за период: время online ÷ (период − ремонт и прочие статусы). Период начинается не раньше первой
// online-записи среди устройств: если площадку подключили позже начала периода, дни без данных — не простой.
// Окна сбоев мониторинга (журнал monitoring_outages) исключаются из периода: в это время данных не было
// по нашей причине, это не простой асика и не подтверждённая работа.
export const computeUptime = (devicesUptime, dateRange, outages = []) => {
  const now = Date.now();
  const end = Math.min(dateRange?.end ? toDate(dateRange.end).getTime() : now, now);
  let start = dateRange?.start ? toDate(dateRange.start).getTime() : end - 24 * 3600 * 1000;
  const active = (devicesUptime || []).filter((d) => Array.isArray(d) && d.length > 0);
  if (active.length === 0) return { percent: null, since: null };

  let firstOnline = Infinity;
  for (const recs of active) {
    for (const r of recs) {
      if (r.status === 'online' || r.status === true) firstOnline = Math.min(firstOnline, new Date(r.started_at).getTime());
    }
  }
  let since = null;
  if (firstOnline !== Infinity && firstOnline > start + 3600 * 1000) {
    start = firstOnline;
    since = new Date(firstOnline);
  }
  if (end <= start) return { percent: null, since };

  const blind = (outages || [])
    .map((o) => ({ start: Math.max(new Date(o.started_at).getTime(), start), end: Math.min(new Date(o.ended_at).getTime(), end) }))
    .filter((o) => o.end > o.start);

  let online = 0;
  let available = 0;
  for (const recs of active) {
    const on = [];
    const excluded = [...blind];
    // Асик, подключённый позже начала периода, считается с момента подключения (первой записи статуса):
    // дни до подключения — не его простой (30 дней на проде 21.10 давали 74,81% из-за подключения 27–29.09)
    const firstRecord = Math.min(...recs.map((r) => new Date(r.started_at).getTime()).filter(Number.isFinite));
    if (Number.isFinite(firstRecord) && firstRecord > start) excluded.push({ start, end: Math.min(firstRecord, end) });
    for (const r of recs) {
      const s = Math.max(new Date(r.started_at).getTime(), start);
      const e = Math.min(r.ended_at ? new Date(r.ended_at).getTime() : now, end);
      if (e <= s) continue;
      if (r.status === 'online' || r.status === true) on.push({ start: s, end: e });
      else if (r.status !== 'offline' && r.status !== false) excluded.push({ start: s, end: e });
    }
    online += mergeDuration(on);
    available += Math.max(0, end - start - mergeDuration(excluded));
  }
  const percent = available > 0 ? Math.min(100, (online / available) * 100) : null;
  return { percent: percent == null ? null : Math.round(percent * 100) / 100, since, blindMinutes: Math.round(mergeDuration(blind) / 60000) };
};

// Окна без данных на общем графике: подряд идущие точки без значения. Окно тянется от первой пустой точки
// до следующей точки со значением (или до края периода). outage — пересекается со сбоем мониторинга из журнала.
// silence — окна, когда молчали все агенты (пинг раз в 10 с). Если известно, пропуск делится: часть внутри
// молчания — kind 'nolink' (площадка без связи, штриховка), вне — 'down' (агент на связи, асики не отвечают:
// остановка площадки, рисуем нулём). Без silence — 'nodata', как раньше.
export const findGaps = (points, x0, x1, outages = [], silence = undefined) => {
  const gaps = [];
  let open = null;
  points.forEach((p, i) => {
    if (p.v == null) {
      if (!open) open = { from: i === 0 ? Math.min(x0, p.t) : p.t };
    } else if (open) {
      gaps.push({ ...open, to: p.t });
      open = null;
    }
  });
  if (open) gaps.push({ ...open, to: x1 });
  const spans = (outages || []).map((o) => [new Date(o.started_at).getTime(), new Date(o.ended_at).getTime()]);
  const marked = gaps.filter((g) => g.to > g.from).map((g) => ({ ...g, outage: spans.some(([a, b]) => a < g.to && b > g.from) }));
  if (!Array.isArray(silence)) return marked.map((g) => ({ ...g, kind: 'nodata' }));
  const out = [];
  const firstValue = points.find((p) => p.v != null)?.t;
  for (const g of marked) {
    // пропуск в начале периода, до первого показания: асики могли быть ещё не подключены (агент при этом
    // пинговал, как 21–25.09 на проде) — это не остановка, оставляем «Нет данных»
    if (firstValue === undefined || g.to <= firstValue) { out.push({ ...g, kind: 'nodata' }); continue; }
    let t = g.from;
    const inside = silence.filter((s) => s.to > g.from && s.from < g.to).sort((a, b) => a.from - b.from);
    for (const s of inside) {
      const a = Math.max(s.from, g.from), b = Math.min(s.to, g.to);
      if (a > t) out.push({ from: t, to: a, outage: g.outage, kind: 'down' });
      out.push({ from: a, to: b, outage: g.outage, kind: 'nolink' });
      t = Math.max(t, b);
    }
    if (g.to > t) out.push({ from: t, to: g.to, outage: g.outage, kind: 'down' });
  }
  // короткие кусочки (меньше 2 мин) на стыке — шум между пингом и последним показанием
  return out.filter((g) => g.to - g.from >= 120000);
};

const fmtInt = (n) => Math.round(n).toLocaleString('ru-RU');
const fmtDay = (d) => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;

const HashrateChart = ({ series, algorithm, colors, dateRange, t, outages = [], silence }) => {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  // Ширина — по контейнеру, высота постоянная: график не меняет высоту блока и не растягивает подписи
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width || 0);
      if (w > 0) setW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 128, L = 8, R = 8, T = 10, B = 24; // ниже прежних 148: под графиком строка легенды

  const points = useMemo(() => (series?.data || []).map((p) => ({
    t: p.time instanceof Date ? p.time.getTime() : new Date(p.time).getTime(),
    v: p.isNull || p.hashrate == null || !Number.isFinite(Number(p.hashrate)) ? null : Number(p.hashrate),
  })).filter((p) => !isNaN(p.t)), [series]);

  const withValue = points.filter((p) => p.v != null);
  if (withValue.length < 2) {
    return <Box ref={wrapRef} sx={{ height: H + 24, display: 'grid', placeItems: 'center', color: colors.muted, fontSize: 13 }}>{t('summaryChartEmpty')}</Box>;
  }

  const x0 = dateRange?.start ? toDate(dateRange.start).getTime() : points[0].t;
  const x1 = Math.max(dateRange?.end ? toDate(dateRange.end).getTime() : points[points.length - 1].t, points[points.length - 1].t);
  const maxV = Math.max(...withValue.map((p) => p.v)) || 1;
  const yMax = maxV * 1.08;
  const sx = (ts) => L + ((ts - x0) / Math.max(1, x1 - x0)) * (W - L - R);
  const sy = (v) => T + (1 - v / yMax) * (H - T - B);

  // разрывы там, где данных нет
  const segments = [];
  let cur = [];
  for (const p of points) {
    if (p.v == null) { if (cur.length) segments.push(cur); cur = []; } else cur.push(p);
  }
  if (cur.length) segments.push(cur);

  // Окна без данных: ни одной записи хешрейта ни у одного асика. Это не простой (простой — линия в нуле,
  // агент шлёт нулевой хешрейт), а время, когда Мониторинг не получал показаний. Показываем полосой.
  const gaps = findGaps(points, x0, x1, outages, silence);

  const last = withValue[withValue.length - 1];
  const min = withValue.reduce((m, p) => (p.v < m.v ? p : m), withValue[0]);
  const shown = hover && !hover.gap ? hover : last;
  const spanDays = (x1 - x0) / 86400000;
  const fmtTime = (ts) => {
    const d = new Date(ts);
    const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return spanDays > 2 ? `${fmtDay(d)} ${hm}` : hm;
  };
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((k) => x0 + k * (x1 - x0));

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const ts = x0 + ((e.clientX - rect.left) / rect.width * W - L) / (W - L - R) * (x1 - x0);
    const gap = gaps.find((g) => ts >= g.from && ts <= g.to);
    if (gap) { setHover({ gap }); return; }
    let best = withValue[0];
    for (const p of withValue) if (Math.abs(p.t - ts) < Math.abs(best.t - ts)) best = p;
    setHover(best);
  };

  return (
    <Box ref={wrapRef} sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', fontSize: 13, mb: 0.5, minHeight: 20 }}>
        {hover && hover.gap ? (
          <span style={{ color: colors.muted }}>
            <b style={{ color: hover.gap.kind === 'down' ? colors.bad : colors.warn }}>
              {t(hover.gap.kind === 'down' ? 'summaryGapDown' : hover.gap.outage ? 'summaryGapOutage' : hover.gap.kind === 'nolink' ? 'summaryGapNoLink' : 'summaryGapNoData')}
            </b>{' '}
            {fmtTime(hover.gap.from)} – {fmtTime(hover.gap.to)}
          </span>
        ) : (
        <span>
          <b>{formatHashrateTh(shown.v, algorithm)}</b>{' '}
          <span style={{ color: colors.muted }}>{hover ? fmtTime(hover.t) : t('summaryNow')}</span>
        </span>
        )}
        <span style={{ color: colors.muted }}>
          {t('summaryMin')} {formatHashrateTh(min.v, algorithm)} · {fmtTime(min.t)}
        </span>
      </Box>
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', maxWidth: '100%' }} onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label={t('summaryChartTitle')}>
        {[0, 0.5, 1].map((k) => (
          <g key={k}>
            <line x1={L} x2={W - R} y1={sy(yMax * k / 1.08)} y2={sy(yMax * k / 1.08)} stroke={colors.line} strokeWidth="1" />
            <text x={L + 2} y={sy(yMax * k / 1.08) - 3} fontSize="11" fill={colors.faint}>{formatHashrateTh(maxV * k, algorithm)}</text>
          </g>
        ))}
        <defs>
          <pattern id="chart-gap-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke={colors.faint} strokeWidth="2" strokeOpacity="0.45" />
          </pattern>
        </defs>
        {gaps.map((g, i) => (g.kind === 'down' ? (
          <g key={`gap${i}`}>
            <rect x={sx(g.from)} y={T} width={Math.max(2, sx(g.to) - sx(g.from))} height={H - T - B} fill={colors.bad} fillOpacity="0.08" />
            <line x1={sx(g.from)} x2={sx(g.to)} y1={sy(0)} y2={sy(0)} stroke={colors.bad} strokeWidth="2.5" />
          </g>
        ) : (
          <rect key={`gap${i}`} x={sx(g.from)} y={T} width={Math.max(2, sx(g.to) - sx(g.from))} height={H - T - B}
            fill="url(#chart-gap-hatch)" stroke={g.outage ? colors.warn : 'none'} strokeOpacity="0.5" />
        )))}
        {segments.map((seg, i) => (
          <g key={i}>
            <polygon fill={colors.chart} fillOpacity="0.12" points={`${sx(seg[0].t)},${sy(0)} ${seg.map((p) => `${sx(p.t)},${sy(p.v)}`).join(' ')} ${sx(seg[seg.length - 1].t)},${sy(0)}`} />
            <polyline fill="none" stroke={colors.chart} strokeWidth="2" strokeLinejoin="round" points={seg.map((p) => `${sx(p.t)},${sy(p.v)}`).join(' ')} />
          </g>
        ))}
        <circle cx={sx(shown.t)} cy={sy(shown.v)} r="4" fill={colors.chart} />
        {hover && !hover.gap && <line x1={sx(hover.t)} x2={sx(hover.t)} y1={T} y2={H - B} stroke={colors.faint} strokeWidth="1" strokeDasharray="3 3" />}
        {ticks.map((ts, i) => (
          <text key={i} x={Math.min(Math.max(sx(ts), L + 16), W - R - 16)} y={H - 6} fontSize="11" fill={colors.faint} textAnchor="middle">
            {spanDays > 2 ? fmtDay(new Date(ts)) : fmtTime(ts)}
          </text>
        ))}
      </svg>
      {gaps.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', alignItems: 'center', gap: '14px', fontSize: 11, lineHeight: '16px', color: colors.muted }}>
          {gaps.some((g) => g.kind !== 'down') && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <svg width="14" height="10" aria-hidden="true"><rect width="14" height="10" fill="url(#chart-gap-hatch)" /></svg>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(Array.isArray(silence) ? 'summaryGapLegendNoLink' : 'summaryGapLegend')}</span>
            </span>
          )}
          {gaps.some((g) => g.kind === 'down') && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <svg width="14" height="10" aria-hidden="true"><line x1="0" x2="14" y1="8" y2="8" stroke={colors.bad} strokeWidth="2.5" /></svg>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('summaryGapLegendDown')}</span>
            </span>
          )}
        </Box>
      )}
    </Box>
  );
};

const SummaryPanel = ({ devices, uptimeByDevice, graphData, hashrateByAlgorithm, dateRange, workSummary, onDeviceClick, chartError = false, chartLoading = false, onChartRetry, agentSilence }) => {
  const theme = useTheme();
  const { t } = useTranslation('dashboard');
  const dark = theme.palette.mode === 'dark';
  const colors = {
    tile: dark ? '#1c1c20' : '#eeeef7',
    panel: theme.palette.background.paper,
    line: dark ? '#2b2b31' : '#dedeeb',
    muted: dark ? '#a1a1ad' : '#62627d',
    faint: dark ? '#6f6f7c' : '#9a9ab0',
    ok: theme.palette.contrast.green,
    bad: theme.palette.contrast.red,
    warn: dark ? '#f0b03c' : '#b26a00',
    rep: theme.palette.contrast.gray,
    chart: theme.palette.contrast.green,
  };

  const fleet = useMemo(() => {
    const f = { total: 0, hashing: 0, idle: 0, repair: 0, other: 0 };
    for (const d of devices || []) {
      const status = String(d.status || '').toLowerCase();
      f.total += 1;
      if (status === 'repair') f.repair += 1;
      else if (status === 'online' && Number(d.hashrate) > 0) f.hashing += 1;
      else if (status === 'online' || status === 'offline') f.idle += 1;
      else f.other += 1;
    }
    return f;
  }, [devices]);

  const uptime = useMemo(() => computeUptime(uptimeByDevice, dateRange, workSummary?.outages), [uptimeByDevice, dateRange, workSummary]);

  const money = useMemo(() => {
    if (!workSummary?.devices) return null;
    const byId = new Map(workSummary.devices.map((d) => [d.id, d]));
    let nowW = 0, kwh = 0, amount = 0, known = 0;
    const prices = new Set();
    for (const d of devices || []) {
      const w = byId.get(d.id);
      if (!w) continue;
      known += 1;
      if (String(d.status || '').toLowerCase() === 'online' && Number(d.hashrate) > 0) nowW += Number(w.power) || 0;
      kwh += w.kwh || 0;
      amount += w.amount || 0;
      const price = workSummary.kwh_price?.[w.hosting_id];
      if (price != null) prices.add(price);
    }
    return { nowKw: nowW / 1000, kwh, amount, known, price: prices.size === 1 ? [...prices][0] : null };
  }, [workSummary, devices]);

  // «От нормы»: суммарный текущий хешрейт ÷ суммарная норма. Норма асика — профиль прошивки, а если его нет —
  // номинал позиции (norm_hashrate из work_hours/summary). Асик, который стоит, даёт ноль при полной норме;
  // машины в ремонте не считаются. used / total — по скольким асикам алгоритма норма известна.
  const normByAlgorithm = useMemo(() => {
    if (!workSummary?.devices) return {};
    const byId = new Map(workSummary.devices.map((d) => [d.id, d]));
    const acc = {};
    for (const d of devices || []) {
      const status = String(d.status || '').toLowerCase();
      if (status === 'repair') continue;
      const a = acc[d.algorithm] || (acc[d.algorithm] = { hr: 0, norm: 0, used: 0, total: 0 });
      a.total += 1;
      const w = byId.get(d.id);
      const norm = w?.norm_hashrate ?? w?.profile_hashrate;
      const k = UNIT_TO_H[d.hashrate_unit];
      if (!norm || !k) continue;
      a.used += 1;
      a.norm += norm * k;
      if (status === 'online') a.hr += (Number(d.hashrate) || 0) * k;
    }
    return Object.fromEntries(Object.entries(acc).map(([alg, a]) => [alg, {
      pct: a.norm > 0 ? Math.round((a.hr / a.norm) * 100) : null, used: a.used, total: a.total,
    }]));
  }, [workSummary, devices]);

  // Требуют внимания: асик стоит (нет связи или нет хешрейта) либо выдаёт меньше 85% своей нормы. Ремонт не считается.
  const attention = useMemo(() => {
    const byId = new Map((workSummary?.devices || []).map((d) => [d.id, d]));
    const list = [];
    for (const d of devices || []) {
      const status = String(d.status || '').toLowerCase();
      if (status !== 'online' && status !== 'offline') continue;
      const hr = Number(d.hashrate) || 0;
      const norm = byId.get(d.id)?.norm_hashrate ?? byId.get(d.id)?.profile_hashrate ?? null;
      if (status === 'offline') list.push({ ...d, reason: 'offline', order: 0 });
      else if (hr <= 0) list.push({ ...d, reason: 'idle', order: 1 });
      else if (norm && hr < norm * 0.85) list.push({ ...d, reason: 'low', pct: Math.round((hr / norm) * 100), order: 2 });
    }
    return list.sort((a, b) => a.order - b.order || (a.pct || 0) - (b.pct || 0));
  }, [devices, workSummary]);

  const algos = (hashrateByAlgorithm || []).filter((a) => a.totalDevices > 0).sort((x, y) => y.totalDevices - x.totalDevices);
  const [algoPick, setAlgoPick] = useState(null);
  const biggest = algos.reduce((m, a) => (!m || a.totalDevices > m.totalDevices ? a : m), null);
  const algorithm = algos.some((a) => a.algorithm === algoPick) ? algoPick : biggest?.algorithm;
  const series = (graphData || []).find((g) => g.algorithm === algorithm);

  const tile = { background: colors.tile, borderRadius: '10px', padding: '11px 13px', minWidth: 0, minHeight: 92, boxSizing: 'border-box' };
  const label = { fontSize: 12, color: colors.muted };
  const value = { fontSize: 23, fontWeight: 600, lineHeight: 1.25, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  const sub = { fontSize: 12, color: colors.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const dot = (c) => ({ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 5, background: c });

  return (
    <Box sx={{ px: '20px', display: 'flex', flexDirection: 'column', gap: '10px', fontVariantNumeric: 'tabular-nums' }}>
      <Box sx={{ display: 'grid', gap: '10px', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2,minmax(0,1fr))', md: 'minmax(0,2.1fr) repeat(3,minmax(0,1fr))' } }}>
        <Box sx={{ ...tile, gridColumn: { sm: '1 / -1', md: 'auto' } }}>
          <div style={label}>{t('summaryAsics')}</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap' }}>
            <div style={value}>{fleet.total}</div>
            <Box sx={{ display: 'flex', gap: '4px 16px', flexWrap: { xs: 'wrap', md: 'nowrap' }, whiteSpace: 'nowrap', fontSize: 13, color: colors.muted }}>
              <span><i style={dot(colors.ok)} /><b style={{ color: theme.palette.text.primary }}>{fleet.hashing}</b> {t('summaryHashing')}</span>
              <span><i style={dot(colors.bad)} /><b style={{ color: theme.palette.text.primary }}>{fleet.idle}</b> {t('summaryIdle')}</span>
              <span><i style={dot(colors.rep)} /><b style={{ color: theme.palette.text.primary }}>{fleet.repair}</b> {t('summaryRepair')}</span>
              {fleet.other > 0 && <span><b style={{ color: theme.palette.text.primary }}>{fleet.other}</b> {t('summaryOther')}</span>}
            </Box>
          </Box>
          {(
            <Box sx={{ display: 'flex', gap: '2px', height: 6, mt: '9px', borderRadius: '3px', overflow: 'hidden' }}>
              {fleet.hashing > 0 && <i style={{ flex: fleet.hashing, minWidth: 6, background: colors.ok }} />}
              {fleet.idle > 0 && <i style={{ flex: fleet.idle, minWidth: 6, background: colors.bad }} />}
              {fleet.repair + fleet.other > 0 && <i style={{ flex: fleet.repair + fleet.other, minWidth: 6, background: colors.rep }} />}
            </Box>
          )}
        </Box>

        <Box sx={tile}>
          <div style={label}>{t('summaryUptime')}</div>
          <div style={{ ...value, color: uptime.percent == null ? colors.muted : uptime.percent >= 90 ? colors.ok : uptime.percent >= 70 ? colors.warn : colors.bad }}>
            {uptime.percent == null ? '—' : `${uptime.percent.toLocaleString('ru-RU')}%`}
          </div>
          <div style={sub}>
            {uptime.blindMinutes > 0
              ? t('summaryOutageExcluded', { hours: Math.floor(uptime.blindMinutes / 60), minutes: uptime.blindMinutes % 60 })
              : uptime.since ? t('summaryDataSince', { date: fmtDay(uptime.since) }) : t('summaryForPeriod')}
          </div>
        </Box>

        <Box sx={tile}>
          <div style={label}>{t('summaryPower')}</div>
          <div style={value}>{money ? `${fmtInt(money.nowKw)} ${t('summaryKw')}` : '—'}</div>
          <div style={sub}>{money ? t('summaryKwhPeriod', { value: fmtInt(money.kwh) }) : t('summaryNoData')}</div>
        </Box>

        <Box sx={tile}>
          <div style={label}>{t('summaryAmount')}</div>
          <div style={value}>{money ? `${fmtInt(money.amount)} ₽` : '—'}</div>
          <div style={sub}>
            {!money ? t('summaryNoData')
              : money.price != null ? t('summaryPrice', { price: money.price.toLocaleString('ru-RU') })
              : workSummary?.days_with_data ? t('summaryDays', { count: workSummary.days_with_data }) : t('summaryNoData')}
          </div>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: '10px', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1.5fr)' } }}>
        <Box sx={{ background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: '12px', p: '12px 14px', minWidth: 0, height: { md: 232 }, overflow: 'hidden', boxSizing: 'border-box' }}>
          <Typography sx={{ fontSize: 12, color: colors.muted, mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('summaryByAlgorithm')}</span>
            <Tooltip title={t('summaryNormHint')} arrow>
              <span style={{ borderBottom: `1px dotted ${colors.faint}`, cursor: 'help' }}>{t('summaryOfNorm')}</span>
            </Tooltip>
          </Typography>
          {algos.length === 0 && <Box sx={{ color: colors.muted, fontSize: 13, py: 2 }}>{t('summaryNoData')}</Box>}
          {algos.map((a, i) => (
            <Box key={a.algorithm} sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 12px', alignItems: 'baseline', py: '7px', borderTop: i ? `1px solid ${colors.line}` : 0, fontSize: 13.5 }}>
              <span>{a.algorithm} <span style={{ color: colors.muted, fontSize: 12 }}>{t('summaryOnlineOf', { online: a.onlineDevices, total: a.totalDevices })}</span></span>
              <b>{a.hashrateTh != null ? formatHashrateTh(a.hashrateTh, a.algorithm) : a.hashrate}</b>
              {(() => {
                const n = normByAlgorithm[a.algorithm];
                const partial = n && n.pct != null && n.used < n.total;
                const cell = (
                  <span style={{ fontSize: 12, minWidth: 34, textAlign: 'right', whiteSpace: 'nowrap', color: !n || n.pct == null ? colors.faint : n.pct >= 85 ? colors.ok : colors.bad, cursor: partial ? 'help' : 'default' }}>
                    {!n || n.pct == null ? '—' : `${n.pct}%`}{partial ? '*' : ''}
                  </span>
                );
                return partial ? <Tooltip title={t('summaryNormCoverage', { used: n.used, total: n.total })} arrow>{cell}</Tooltip> : cell;
              })()}
            </Box>
          ))}

        </Box>

        {/* Отдельное окно: прокручивается только список, заголовок и соседние карточки остаются на месте */}
        <Box sx={{ background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: '12px', p: '12px 14px', minWidth: 0, height: { md: 232 }, maxHeight: { xs: 260, md: 'none' }, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: 12, color: colors.muted, mb: 0.5, display: 'flex', justifyContent: 'space-between', flex: '0 0 auto' }}>
            <span>{t('summaryAttention')}</span>
            <b style={{ color: attention.length ? colors.warn : colors.ok }}>{attention.length ? attention.length : t('summaryAttentionNone')}</b>
          </Typography>
          <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', mr: '-6px', pr: '6px' }}>
            {attention.length === 0 && (
              <Box sx={{ height: '100%', minHeight: 60, display: 'grid', placeItems: 'center', color: colors.muted, fontSize: 13 }}>{t('summaryAttentionEmpty')}</Box>
            )}
            {attention.map((d) => (
              <Box
                key={d.id}
                onClick={() => onDeviceClick && onDeviceClick(d.id)}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: '5px', px: '4px', mx: '-4px', fontSize: 13, cursor: onDeviceClick ? 'pointer' : 'default', borderRadius: '6px', '&:hover': { background: colors.tile } }}
              >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                  {d.ip || '—'} <span style={{ color: colors.muted, fontSize: 12 }}>{d.model || ''}</span>
                </span>
                <span style={{ whiteSpace: 'nowrap', color: d.reason === 'low' ? colors.warn : colors.bad, fontSize: 12 }}>
                  {d.reason === 'offline' ? t('summaryReasonOffline') : d.reason === 'idle' ? t('summaryReasonIdle') : t('summaryReasonLow', { pct: d.pct })}
                </span>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: '12px', p: '12px 14px', minWidth: 0, height: { md: 232 }, overflow: 'hidden', boxSizing: 'border-box' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'nowrap', mb: 0.5, minHeight: 30 }}>
            <Typography sx={{ fontSize: 12, color: colors.muted }}>{t('summaryChartTitle')}</Typography>
            {algos.length > 1 && (
              <ToggleButtonGroup size="small" exclusive value={algorithm} onChange={(e, v) => v && setAlgoPick(v)}>
                {algos.map((a) => <ToggleButton key={a.algorithm} value={a.algorithm} sx={{ py: 0.1, px: 1, fontSize: 12, textTransform: 'none' }}>{a.algorithm}</ToggleButton>)}
              </ToggleButtonGroup>
            )}
          </Box>
          {chartLoading ? <Box role="status" sx={{ minHeight: 172, display: 'grid', placeItems: 'center' }}>{t('summaryChartLoading')}</Box>
            : chartError ? <Box role="alert" sx={{ minHeight: 172, display: 'grid', placeItems: 'center', color: colors.bad }}>
              <span>{t('summaryChartLoadError')}</span><Button onClick={onChartRetry}>{t('summaryChartRetry')}</Button>
            </Box> : <HashrateChart series={series} algorithm={algorithm} colors={colors} dateRange={dateRange} t={t} outages={workSummary?.outages} silence={agentSilence} />}
        </Box>
      </Box>
    </Box>
  );
};

export default SummaryPanel;
